import React, { Suspense, type JSX } from "react"
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"

const ExternalMCPMentionComponent = React.lazy(() => import("./component"))

type SerializedExternalMCPMentionNode = Spread<
  {
    serverId: string
    name: string
    slug: string
    requiresReauthorization: boolean
  },
  SerializedLexicalNode
>

export class ExternalMCPMentionNode extends DecoratorNode<JSX.Element> {
  __serverId: string
  __name: string
  __slug: string
  __requiresReauthorization: boolean

  static getType(): string {
    return "external-mcp-mention"
  }

  static clone(node: ExternalMCPMentionNode): ExternalMCPMentionNode {
    return new ExternalMCPMentionNode(
      node.__serverId,
      node.__name,
      node.__slug,
      node.__requiresReauthorization,
      node.__key
    )
  }

  constructor(serverId: string, name: string, slug: string, requiresReauthorization: boolean, key?: NodeKey) {
    super(key)
    this.__serverId = serverId
    this.__name = name
    this.__slug = slug
    this.__requiresReauthorization = requiresReauthorization
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span")
    span.style.display = "inline"
    return span
  }

  updateDOM(): false {
    return false
  }

  exportDOM(): DOMExportOutput {
    return { element: document.createTextNode(`@${this.__slug}`) as unknown as HTMLElement }
  }

  isInline(): boolean {
    return true
  }

  isKeyboardSelectable(): boolean {
    return false
  }

  getTextContent(): string {
    return `@${this.__slug}`
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={<span>{this.__name}</span>}>
        <ExternalMCPMentionComponent name={this.__name} requiresReauthorization={this.__requiresReauthorization} />
      </Suspense>
    )
  }

  exportJSON(): SerializedExternalMCPMentionNode {
    return {
      ...super.exportJSON(),
      type: "external-mcp-mention",
      serverId: this.__serverId,
      name: this.__name,
      slug: this.__slug,
      requiresReauthorization: this.__requiresReauthorization,
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedExternalMCPMentionNode): ExternalMCPMentionNode {
    return $createExternalMCPMentionNode({
      id: serializedNode.serverId,
      name: serializedNode.name,
      slug: serializedNode.slug,
      requires_reauthorization: serializedNode.requiresReauthorization,
    })
  }
}

export function $createExternalMCPMentionNode(server: {
  id: string
  name: string
  slug: string
  requires_reauthorization: boolean
}): ExternalMCPMentionNode {
  return $applyNodeReplacement(
    new ExternalMCPMentionNode(server.id, server.name, server.slug, server.requires_reauthorization)
  )
}

export function $isExternalMCPMentionNode(node: LexicalNode | null | undefined): node is ExternalMCPMentionNode {
  return node instanceof ExternalMCPMentionNode
}
